import React from 'react';
import { DICE_SIDES, DiceType } from '../../types/RoomMode';
import { RollGroup } from '../../utils/roomClient/types';

export type ExplodeChain = number[];

interface Props {
    chains: ExplodeChain[];
    /** Brío mode: difficulty threshold for counting successes */
    difficulty?: number | null;
    /** Only-dice mode single type: all tiles use this shape */
    diceType?: DiceType;
    /** Only-dice mode multi-type: each group renders with its own shape */
    rollGroups?: RollGroup[];
    className?: string;
}

function countSuccesses(chains: ExplodeChain[], difficulty?: number | null) {
    if (!difficulty) return 0;
    return chains.reduce((acc, chain) => acc + chain.reduce((a, v) => a + (v >= difficulty ? 1 : 0), 0), 0);
}

// Text centroid y-positions for each die shape (SVG viewBox 0 0 100 100)
const TILE_TEXT_Y: Record<DiceType, number> = {
    d4: 63,
    d6: 54,
    d8: 54,
    d10: 54,
    d12: 53,
    d20: 54,
    d100: 54,
    df: 54,
};

function fudgeLabel(v: number): string {
    if (v === 3) return '+';
    if (v === 1) return '−';
    return '';
}

function DiceTile({ value, type, isExplosion }: { value: number; type: DiceType; isExplosion?: boolean }) {
    const sharedShape = { className: 'die-tile-shape' };
    const shape = {
        d4:   <polygon points="50,8 94,92 6,92" {...sharedShape} />,
        d6:   <rect x="12" y="12" width="76" height="76" rx="8" {...sharedShape} />,
        d8:   <polygon points="50,6 94,50 50,94 6,50" {...sharedShape} />,
        d10:  <polygon points="50,6 90,42 72,92 28,92 10,42" {...sharedShape} />,
        d12:  <polygon points="50,6 93,36 77,90 23,90 7,36" {...sharedShape} />,
        d20:  <polygon points="50,6 88,28 88,72 50,94 12,72 12,28" {...sharedShape} />,
        d100: <polygon points="50,7 84,23 92,60 69,89 31,89 8,60 16,23" {...sharedShape} />,
        df:   <rect x="12" y="12" width="76" height="76" rx="8" {...sharedShape} />,
    }[type];
    const label = type === 'df' ? fudgeLabel(value) : String(value);
    const fontSize = label.length <= 1 ? 42 : label.length === 2 ? 32 : 22;

    return (
        <svg
            viewBox="0 0 100 100"
            width="38"
            height="38"
            className={`die-tile${isExplosion ? ' die-tile--explosion' : ''}`}
            aria-label={`dado ${value}`}
        >
            {shape}
            <text
                x="50"
                y={TILE_TEXT_Y[type]}
                textAnchor="middle"
                dominantBaseline="middle"
                className={`die-tile-text${isExplosion ? ' die-tile-text--explosion' : ''}`}
                fontSize={fontSize}
            >
                {label}
            </text>
        </svg>
    );
}

const DiceResult: React.FC<Props> = ({ chains, difficulty, diceType, rollGroups, className }) => {
    const isOnlyDice = diceType !== undefined || (rollGroups !== undefined && rollGroups.length > 0);
    const successes = isOnlyDice ? null : countSuccesses(chains, difficulty);

    if (rollGroups && rollGroups.length > 1) {
        return (
            <div className={`dice-result ${className ?? ''}`}>
                <div className="chain">
                    {rollGroups.map((group) => {
                        const maxSides = DICE_SIDES[group.diceType];
                        return group.chains.map((chain, i) =>
                            chain.map((v, idx) => (
                                <DiceTile
                                    key={`${group.diceType}-${i}-${idx}`}
                                    value={v}
                                    type={group.diceType}
                                    isExplosion={v === maxSides && chain.length > 1}
                                />
                            ))
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className={`dice-result ${className ?? ''}`}>
            {isOnlyDice && diceType ? (
                <div className="dice-result-type-label">{diceType}</div>
            ) : null}
            <div className="chain" aria-hidden={chains.length === 0}>
                {chains.map((chain, i) => (
                    <React.Fragment key={i}>
                        {chain.map((v, idx) => {
                            if (isOnlyDice && diceType) {
                                const isExplosion = v === DICE_SIDES[diceType] && chain.length > 1;
                                return <DiceTile key={`${i}-${idx}`} value={v} type={diceType} isExplosion={isExplosion} />;
                            }
                            // Brío mode: always d6 with exploding, every 6 is an explosion
                            const isExplosion = v === 6;
                            return (
                                <div key={`${i}-${idx}`} className="die-visual" aria-label={`dado ${v}`}>
                                    <span className={isExplosion ? 'explosion' : ''}>{v}</span>
                                </div>
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>

            {!isOnlyDice && typeof difficulty === 'number' ? (
                <div style={{ marginTop: 8, fontWeight: 600 }}>
                    Total éxitos: {successes}
                </div>
            ) : null}
        </div>
    );
};

export default DiceResult;
